import { useSyncExternalStore } from 'react';

/**
 * Is a call up on the console's OWN softphone right now?
 *
 * A module-level store rather than context, because the two ends of this
 * question sit on opposite sides of the module boundary. The device lives
 * INSIDE CommsProvider — it reads the inbox list and the agent's calling
 * grants from it — while the thing that has to ask is the ROUTE around it:
 * navigating away unmounts the provider, and unmounting the device destroys
 * it, which disconnects every call it is holding. Standalone, "Channels" is
 * one click from the thread the agent is talking into, so that click hung up
 * on the customer with no warning at all.
 *
 * Embedded in the dashboard there is nothing to guard and nothing writes here:
 * the phone lives in a frame of its own precisely so that moving around the
 * dashboard cannot reach it (see useHostPhone).
 */
let live = false;
const listeners = new Set<() => void>();

/** The softphone reporting whether it is holding a call. */
export function setCallLive(next: boolean): void {
  if (next === live) return;
  live = next;
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => live;

export function useCallLive(): boolean {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
